import React, { useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import Attachment from "~/app/common/Attachment";
import useTranslation from '~/app/hooks/useTranslation';
import { useStoreActions, useStoreState } from '~/store';
import { ATTACHMENT_MAX_FILES, ATTACHMENT_MAX_FILE_SIZE } from '~/workorder/helper';
import { Attachment as WorkOrderAttachmentType, WorkOrderStatuses, WorkOrderTaskDetail } from '~/workorder/types';

export interface AttachmentProps {
    workOrderId: number;
    taskData: WorkOrderTaskDetail;
}

export type TaskAttachmentComponent = React.FC<AttachmentProps>;

const TaskAttachment: TaskAttachmentComponent = ({ workOrderId, taskData }) => {

    const workOrderStatus = useStoreState(state => state.workOrder.data.status);
    const netWorkStatus = useStoreState(state => state.app.offline.status);
    const attachmentSaving = useStoreState(state => state.workOrder.attachmentSaving);
    const allowedFileExtensions = useStoreState(state => state.machines.allowedWorkOrderFileExtensions);
    const { saveIssueServiceAttachment, deleteIssueServiceAttachment } = useStoreActions(state => state.workOrder);
    const { getWorkOrderTaskDetail } = useStoreActions(state => state.workOrder);
    const { t } = useTranslation('workorder');

    const canEditAttachments = useMemo(() => {
        return (
            workOrderId != null
            && workOrderStatus != null
            && workOrderStatus !== WorkOrderStatuses.APPROVED
            && !netWorkStatus
        );
    }, [workOrderId, workOrderStatus, netWorkStatus]);
    
    const refreshTaskDetail = useCallback(async () => {
        await getWorkOrderTaskDetail({
            id: workOrderId,
            taskId: taskData.id,
            type: taskData.type,
            showLoading: false,
        });
    }, [getWorkOrderTaskDetail, workOrderId, taskData.id, taskData.type]);
    
    const handleAddAttachments = useCallback(async (files: File[]) => {
        try {
            const response = await saveIssueServiceAttachment({
                id: taskData.id,
                type: taskData.type,
                files,
            });
            if (response.success !== false) {
                toast.success(t('detail.attachmentUploadSuccess'));
                await refreshTaskDetail();
            }
        } catch {
            toast.error(t('detail.attachmentUploadError'));
        }
    }, [saveIssueServiceAttachment, taskData.id, taskData.type, refreshTaskDetail, t]);
    
    const handleDeleteAttachment = useCallback(async (attachment: WorkOrderAttachmentType) => {
        try {
            const response = await deleteIssueServiceAttachment({
                id: taskData.id,
                type: taskData.type,
                url: attachment.url,
            });
            if (response.success !== false) {
                toast.success(t('detail.attachmentDeleteSuccess'));
                await refreshTaskDetail();
            }
        } catch {
            toast.error(t('detail.attachmentDeleteError'));
        }
    }, [deleteIssueServiceAttachment, taskData.id, taskData.type, refreshTaskDetail, t]);

    return (
        <Attachment
            attachments={taskData.attachments ?? []}
            maxFiles={ATTACHMENT_MAX_FILES}
            maxFileSize={ATTACHMENT_MAX_FILE_SIZE}
            allowedFileExtensions={allowedFileExtensions}
            canEdit={canEditAttachments}
            saving={attachmentSaving}
            onAdd={handleAddAttachments}
            onDelete={handleDeleteAttachment}
        />
    );
};

export default TaskAttachment;

